import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";

import { type RewardOffer } from "./types";
import {
  selectAllCardLinkedOffers,
  selectCardLinkedOffersEnrollment,
  selectRecommendedCardLinkedOffers,
  selectCardLinkedOfferCategories,
  selectCardLinkedOfferHistory,
  selectCardLinkedOfferCardId,
} from "./selectors";
import {
  useFetchCardLinkedOffersEnrollment,
  useFetchCardLinkedOffers,
} from "./requests";

/**
 * Intended to fetch both recommended and then allAvailable offers
 */
export const useFetchAllOffersContainer = () => {
  const { fetchEnrollmentStatus, loading: enrollmentLoading } =
    useFetchCardLinkedOffersEnrollment();
  const { fetchOffers, loading: fetchOffersLoading } =
    useFetchCardLinkedOffers();

  const fetchEnrollmentAndOffers = useCallback(() => {
    fetchEnrollmentStatus({
      onData: ({ is_enrolled }) => {
        if (is_enrolled) {
          fetchOffers(true);
          fetchOffers();
        }
      },
    });
  }, [fetchEnrollmentStatus, fetchOffers]);

  return useObjectMemo({
    fetchEnrollmentAndOffers,
    enrollmentLoading,
    fetchOffersLoading,
  });
};

export const useCardLinkedOffersEnrollmentStatus = (): boolean => {
  return useSelector(selectCardLinkedOffersEnrollment);
};

export const useAllCardLinkedOffers = (): RewardOffer[] => {
  const offers = useSelector(selectAllCardLinkedOffers);

  // check if this is a problem??????
  // should likely set unique to state!
  return useMemo(() => {
    const uniqueOffers = new Map<string, RewardOffer>();

    // Use a Map to ensure distinct offers by ID
    offers.forEach((offer) => {
      uniqueOffers.set(offer.id, offer);
    });

    return Array.from(uniqueOffers.values());
  }, [offers]);
};

export const useRecommendedCardLinkedOffers = (): RewardOffer[] => {
  return useSelector(selectRecommendedCardLinkedOffers);
};

export const useCardLinkedOfferCategories = () => {
  return useSelector(selectCardLinkedOfferCategories);
};

export const useCardLinkedOfferHistory = () => {
  return useSelector(selectCardLinkedOfferHistory);
};

export const useCardLinkedOfferSelectedCardId = () => {
  return useSelector(selectCardLinkedOfferCardId);
};

/**
 * Hook to get a specific offer by ID
 * @param offerId - The ID of the offer to find
 * @returns The offer if found, undefined otherwise
 */
export const useOfferById = (offerId: string): RewardOffer | undefined => {
  const allOffers = useAllCardLinkedOffers();
  return useMemo(() => {
    return allOffers.find((offer) => offer.id === offerId);
  }, [allOffers, offerId]);
};

/**
 * Hook to get offers filtered by category
 * @param categoryId - The category ID to filter by
 * @returns Array of offers in the specified category
 */
export const useOffersByCategory = (categoryId: string): RewardOffer[] => {
  const allOffers = useAllCardLinkedOffers();
  return useMemo(() => {
    return allOffers.filter((offer) => offer.categories.includes(categoryId));
  }, [allOffers, categoryId]);
};

/**
 * Hook to get unique categories from all existing offer data
 * @returns Array of unique category names sorted alphabetically
 */
export const useOfferCategories = (): string[] => {
  const allOffers = useAllCardLinkedOffers();
  return useMemo(() => {
    const uniqueCategories = new Set(
      allOffers.flatMap((offer) => offer.categories),
    );

    // Convert to array, filter out empty strings, and sort alphabetically
    return Array.from(uniqueCategories)
      .filter((category) => category && category.trim().length > 0)
      .sort();
  }, [allOffers]);
};

/**
 * Hook for filtering offers with search and category functionality
 * @param searchTerm - The search term to filter by
 * @param selectedFilter - The selected category filter ("All" or category name)
 * @param onFilterChange - Optional callback when filter changes (for UI-specific logic like scrolling)
 * @returns Object with filtered offers and filter management functions
 */
export const useOfferFiltering = (
  searchTerm: string = "",
  selectedFilter: string = "All",
  onFilterChange?: (filter: string, index: number) => void,
) => {
  const availableOffers = useAllCardLinkedOffers();
  const categories = useOfferCategories();

  // Generate filter list with "All" option
  const filters = useMemo(() => {
    return ["All", ...categories];
  }, [categories]);

  // Filter offers based on search term and category
  const filteredOffers = useMemo(() => {
    let filtered = availableOffers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (offer) =>
          offer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          offer.description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply category filter using API categories directly
    if (selectedFilter !== "All") {
      filtered = filtered.filter((offer) =>
        offer.categories.includes(selectedFilter),
      );
    }

    return filtered;
  }, [availableOffers, searchTerm, selectedFilter]);

  // Handle filter change with toggle functionality and optional callback
  const handleFilterChange = useCallback(
    (filter: string, index: number) => {
      // If clicking on the already selected filter (and it's not "All"), toggle it off
      const finalFilter =
        selectedFilter === filter && filter !== "All" ? "All" : filter;
      const finalIndex = finalFilter === "All" ? 0 : index;

      onFilterChange?.(finalFilter, finalIndex);
    },
    [onFilterChange, selectedFilter],
  );

  return useObjectMemo({
    filteredOffers,
    filters,
    handleFilterChange,
  });
};
